import { env } from './env';

// Client for the anonymous Express Interest / Request Callback Edge
// (buyer-request-create, ADR-053). Mirrors contact-api.ts: the authenticated
// flow keeps using createBuyerRequest (RLS-gated); this is only for visitors
// without an account.

const ENDPOINT_PATH = '/functions/v1/buyer-request-create';

export type BuyerRequestErrorCode =
  | 'invalid_payload'
  | 'recaptcha_failed'
  | 'deal_not_found'
  | 'rate_limited'
  | 'recaptcha_unreachable'
  | 'internal_error'
  | 'method_not_allowed'
  | 'network';

export type BuyerRequestResult =
  | { ok: true; referenceId: string }
  | { ok: false; code: BuyerRequestErrorCode };

export type PostBuyerRequestInput = {
  dealId: string;
  type: 'interest' | 'callback';
  fullName: string;
  email: string;
  phone: string;
  message?: string | null;
  callbackSlot?: string | null;
  callbackDate?: string | null;
  recaptchaToken: string;
  locale: string;
};

export async function postBuyerRequest(input: PostBuyerRequestInput): Promise<BuyerRequestResult> {
  if (!env.supabase.url || !env.supabase.anonKey) {
    return { ok: false, code: 'internal_error' };
  }

  let resp: Response;
  try {
    resp = await fetch(`${env.supabase.url}${ENDPOINT_PATH}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: env.supabase.anonKey,
        Authorization: `Bearer ${env.supabase.anonKey}`,
      },
      body: JSON.stringify({
        deal_id: input.dealId,
        type: input.type,
        contact_name: input.fullName,
        contact_email: input.email,
        contact_phone: input.phone,
        message: input.message ?? '',
        callback_slot: input.callbackSlot ?? undefined,
        callback_date: input.callbackDate ?? undefined,
        recaptcha_token: input.recaptchaToken,
        locale: input.locale,
      }),
    });
  } catch {
    return { ok: false, code: 'network' };
  }

  if (resp.status === 201) {
    const body = (await resp.json().catch(() => null)) as { reference_id?: unknown } | null;
    return {
      ok: true,
      referenceId: typeof body?.reference_id === 'string' ? body.reference_id : '',
    };
  }

  const errorBody = (await resp.json().catch(() => null)) as { code?: string } | null;
  return { ok: false, code: mapStatusToCode(resp.status, errorBody?.code) };
}

function mapStatusToCode(status: number, edgeCode: string | undefined): BuyerRequestErrorCode {
  switch (edgeCode) {
    case 'invalid_payload':
    case 'recaptcha_failed':
    case 'deal_not_found':
    case 'rate_limited':
    case 'recaptcha_unreachable':
    case 'internal_error':
    case 'method_not_allowed':
      return edgeCode;
  }
  if (status === 400) return 'invalid_payload';
  if (status === 403) return 'recaptcha_failed';
  if (status === 404) return 'deal_not_found';
  if (status === 405) return 'method_not_allowed';
  if (status === 429) return 'rate_limited';
  if (status === 502) return 'recaptcha_unreachable';
  return 'internal_error';
}
